// The docked question panel from the chat component gallery: a pending
// question and its one-click choices wrap the live composer instead of
// replacing it, so the chat stays open while Phoebe waits for an answer.
// Fully controlled — selection and submission live with the caller.
import { Check, X } from 'lucide-react';
import { type ReactNode, useId } from 'react';

import { ResolvedReferenceText } from '@apps/web/features/phoebe_chat_v3/ui/primitives/reference_badge';
import { cn } from '@apps/web/lib/utils';

export function QuestionComposerPanel({
    question,
    onDismiss,
    children,
    composer,
}: Readonly<{
    readonly question: string;
    readonly onDismiss: () => void;
    /** The choice rows and any multi-choice submit affordance. */
    readonly children?: ReactNode;
    /** The live composer card, nested at the foot of the panel. */
    readonly composer: ReactNode;
}>): ReactNode {
    return (
        <div className="bg-muted @container/question-panel min-w-0 rounded-lg p-1.5">
            <div className="flex items-start justify-between gap-3 py-2.5 pr-2 pl-4">
                <p className="text-v3-chat min-w-0 pt-0.5 font-semibold [overflow-wrap:anywhere]">
                    <ResolvedReferenceText text={question} />
                </p>
                <button
                    type="button"
                    aria-label="Dismiss question"
                    onClick={onDismiss}
                    className="text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring/50 grid size-7 shrink-0 place-items-center rounded-md outline-none focus-visible:ring-[3px]"
                >
                    <X aria-hidden="true" className="size-4" />
                </button>
            </div>
            {children}
            {composer}
        </div>
    );
}

export function QuestionPanelOption({
    letter,
    label,
    description,
    multiple = false,
    selected,
    dimmed = false,
    disabled = false,
    onClick,
}: Readonly<{
    /** Visual row marker (A, B, C, …), mirroring the choice order. */
    readonly letter: string;
    readonly label: string;
    readonly description?: string;
    readonly multiple?: boolean;
    readonly selected: boolean;
    /** True for unchosen rows while a submission is in flight. */
    readonly dimmed?: boolean;
    readonly disabled?: boolean;
    readonly onClick: () => void;
}>): ReactNode {
    const labelId = useId();
    return (
        <button
            type="button"
            aria-labelledby={labelId}
            role={multiple ? 'checkbox' : undefined}
            aria-checked={multiple ? selected : undefined}
            disabled={disabled}
            onClick={onClick}
            className={cn(
                'flex w-full min-w-0 items-center gap-3 rounded-md px-4 py-2 text-left',
                'text-v3-chat',
                'hover:bg-background/70 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                'transition-[background-color,opacity] duration-150 motion-reduce:transition-none',
                dimmed ? 'opacity-30' : 'opacity-100',
                selected && 'bg-agent-action-wash',
            )}
        >
            {multiple ? (
                <span
                    aria-hidden="true"
                    className={cn(
                        'flex size-4 shrink-0 items-center justify-center rounded-[4px] border',
                        selected
                            ? 'border-agent-action bg-agent-action text-white'
                            : 'border-border bg-background',
                    )}
                >
                    {selected ? <Check className="size-3" /> : null}
                </span>
            ) : (
                <span
                    aria-hidden="true"
                    className="text-muted-foreground text-v3-chat-meta w-4 shrink-0 text-center font-medium"
                >
                    {letter}
                </span>
            )}
            <span className="flex min-w-0 flex-1 flex-col gap-0.5 [overflow-wrap:anywhere]">
                <span id={labelId}>
                    <ResolvedReferenceText text={label} />
                </span>
                {description !== undefined && (
                    <span className="text-muted-foreground text-v3-chat-meta">
                        <ResolvedReferenceText text={description} />
                    </span>
                )}
            </span>
        </button>
    );
}
